import { inject, Injectable, NgZone, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { Message, Conversation, ChatRequest, ModelBasedChatResponse } from '../../model/chat.model';
import { AuthService } from '../../api-services/AdminHRService/Login/auth.service';
import { AIenvironment } from '../../../environments/environment';  // ← import
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ChatService {

  // ← use environment instead of hardcoded
  private readonly API         = `${AIenvironment.apiUrl}chat`;
  private readonly HUB         = AIenvironment.hubUrl;
  private readonly MODEL_API   = AIenvironment.modelBasedUrl;

  private ngZone = inject(NgZone);
  private resolvedEmployeeId: number | null = null;
  private hub!: signalR.HubConnection;

  // signals
  conversations    = signal<Conversation[]>([]);
  messages         = signal<Message[]>([]);
  isStreaming      = signal(false);
  isConnected      = signal(false);
  isOpen           = signal(false);
  activeConvId     = signal<string | null>(null);
  errorMsg         = signal<string | null>(null);
  modelMessages    = signal<Message[]>([]);
  isModelStreaming  = signal(false);

  constructor(private http: HttpClient, private auth: AuthService) { }

  private async resolveEmployeeId(): Promise<number | null> {
    if (this.resolvedEmployeeId !== null) return this.resolvedEmployeeId;
    const user = this.auth.getUser();
    if (!user) return null;
    const employeeId = Number(user.userId);
    if (employeeId > 0) {
      this.resolvedEmployeeId = employeeId;
      return this.resolvedEmployeeId;
    }
    return null;
  }

  toggleChat(): void {
    this.isOpen.update(v => !v);
    if (this.isOpen()) {
      this.resolveEmployeeId();
      if (!this.isConnected()) this.connect();
    }
  }

  closeChat(): void { this.isOpen.set(false); }

  async connect(): Promise<void> {
    try {
      this.hub = new signalR.HubConnectionBuilder()
        .withUrl(this.HUB)
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.hub.on('ReceiveToken', (token: string) => {
        this.messages.update(msgs => {
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant' && last.isStreaming)
            return [...msgs.slice(0, -1), { ...last, content: last.content + token }];
          return msgs;
        });
      });

      this.hub.on('ReceiveComplete', () => {
        this.isStreaming.set(false);
        this.messages.update(msgs => {
          const last = msgs[msgs.length - 1];
          if (last?.isStreaming)
            return [...msgs.slice(0, -1), { ...last, isStreaming: false }];
          return msgs;
        });
        this.loadConversations();
      });

      this.hub.onclose(() => this.isConnected.set(false));
      this.hub.onreconnected(() => this.isConnected.set(true));

      await this.hub.start();
      this.isConnected.set(true);
      this.errorMsg.set(null);
      await this.resolveEmployeeId();

    } catch (err) {
      console.warn('SignalR not available — HTTP mode only');
      this.isConnected.set(false);
      await this.resolveEmployeeId();
    }
  }

  async disconnect(): Promise<void> {
    await this.hub?.stop();
    this.isConnected.set(false);
    this.resolvedEmployeeId = null;
  }

  async sendMessage(text: string): Promise<void> {
    if (!text.trim() || this.isStreaming()) return;
    const convId = this.activeConvId() ?? crypto.randomUUID();
    this.activeConvId.set(convId);
    this.messages.update(msgs => [...msgs, { role: 'user', content: text, createdAt: new Date() }]);
    this.messages.update(msgs => [...msgs, { role: 'assistant', content: '', isStreaming: true, createdAt: new Date() }]);
    this.isStreaming.set(true);
    this.errorMsg.set(null);
    const employeeId = await this.resolveEmployeeId();
    const req: ChatRequest = {
      conversationId: convId,
      userId: employeeId?.toString() ?? 'guest',
      message: text,
      employeeId: employeeId ?? undefined
    };
    try {
      await this.hub.invoke('SendMessage', req);
    } catch (err) {
      this.isStreaming.set(false);
      this.errorMsg.set('Failed to send message.');
      this.messages.update(msgs => msgs.slice(0, -1));
    }
  }

  loadConversations(): void {
    const userId = this.resolvedEmployeeId?.toString() ?? this.auth.getUser()?.userId ?? 'guest';
    this.http.get<Conversation[]>(`${this.API}/conversations/${userId}`)
      .subscribe({ next: c => this.conversations.set(c) });
  }

  loadMessages(conversationId: string): void {
    this.activeConvId.set(conversationId);
    this.http.get<Message[]>(`${this.API}/messages/${conversationId}`)
      .subscribe({ next: msgs => this.messages.set(msgs) });
  }

  newConversation(): void { this.activeConvId.set(null); this.messages.set([]); }

  deleteConversation(id: string): void {
    this.http.delete(`${this.API}/conversations/${id}`).subscribe(() => {
      this.conversations.update(c => c.filter(x => x.id !== id));
      if (this.activeConvId() === id) this.newConversation();
    });
  }

  async sendModelMessage(text: string): Promise<void> {
    if (!text.trim() || this.isModelStreaming()) return;
    const employeeId = await this.resolveEmployeeId();

    this.modelMessages.update(msgs => [...msgs, { role: 'user', content: text, createdAt: new Date() }]);
    this.modelMessages.update(msgs => [...msgs, { role: 'assistant', content: '...', isStreaming: true, createdAt: new Date() }]);
    this.isModelStreaming.set(true);
    this.errorMsg.set(null);

    const history = this.modelMessages().slice(-8, -2)
      .filter(m => !m.isStreaming && m.content !== '...')
      .map(m => ({ role: m.role, content: m.content }));

    const req = {
      message: text,
      history,
      employeeId: employeeId?.toString() ?? ''
    };

    console.log('Request:', JSON.stringify(req));

    try {
      const res = await firstValueFrom(
        this.http.post<ModelBasedChatResponse>(this.MODEL_API, req)
      );
      this.ngZone.run(() => {
        this.modelMessages.update(msgs => [
          ...msgs.slice(0, -1),
          { role: 'assistant', content: res.reply, isStreaming: false, createdAt: new Date(), bot: res.bot }
        ]);
        this.isModelStreaming.set(false);
      });
    } catch (err: any) {
      console.error('Error:', err?.status, err?.error);
      this.ngZone.run(() => {
        this.modelMessages.update(msgs => [
          ...msgs.slice(0, -1),
          { role: 'assistant', content: 'Something went wrong. Please try again.', isStreaming: false, createdAt: new Date() }
        ]);
        this.isModelStreaming.set(false);
        this.errorMsg.set('Failed to get response.');
      });
    }
  }

  clearChat(): void { this.modelMessages.set([]); this.errorMsg.set(null); }

  clearSession(): void {
  this.resolvedEmployeeId = null;
  this.modelMessages.set([]);
  this.messages.set([]);
  this.conversations.set([]);
  this.activeConvId.set(null);
  this.errorMsg.set(null);
  this.isOpen.set(false);
  console.log('Chat session cleared');
}
}